import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Video } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { CoachingApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  CANONICAL_MENTORS,
  extractList,
  extractMentorsFromResponse,
  isBookableMentor,
  resolveCoachingMentors,
  toCoachingMentorFormat,
} from '../../data/founderMentor';
import './CoachingMentoring.css';

const personName = (u) => u?.name || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || u?.email || 'Someone';

const defaultWhen = () => {
  const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
  d.setMinutes(0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const sessionLabel = (status) => ({
  requested: 'Waiting on mentor',
  scheduled: 'Waiting on mentor',
  confirmed: 'Accepted — waiting for live room',
  'in-progress': 'Live now',
  completed: 'Completed',
  cancelled: 'Declined',
}[status] || status);

const CoachingMentoring = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mentors, setMentors] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [requesting, setRequesting] = useState(null);
  const [requestBusy, setRequestBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState({ title: '', industry: 'Engineering', yearsOfExperience: 3, expertiseAreas: '', bio: '' });
  const [requestForm, setRequestForm] = useState({ topic: '', when: defaultWhen(), duration: 45 });

  const loadSessions = async () => {
    if (!token) {
      setSessions([]);
      return;
    }
    try {
      const res = await CoachingApi.getMySessions(token, { limit: 20 });
      setSessions(extractList(res, 'sessions'));
    } catch {
      setSessions([]);
    }
  };

  useEffect(() => {
    document.documentElement.classList.add('qn-home-html');
    document.body.classList.add('qn-home-body');
    return () => {
      document.documentElement.classList.remove('qn-home-html');
      document.body.classList.remove('qn-home-body');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await CoachingApi.getMentors({ limit: 50 });
        if (!cancelled) {
          setMentors(resolveCoachingMentors(extractMentorsFromResponse(res)));
        }
      } catch {
        if (!cancelled) setMentors(CANONICAL_MENTORS.map(toCoachingMentorFormat));
      }

      try {
        const res = await CoachingApi.getPrograms({ limit: 12 });
        if (!cancelled) setPrograms(extractList(res, 'programs'));
      } catch {
        if (!cancelled) setPrograms([]);
      }

      if (token) {
        try {
          const res = await CoachingApi.getMyMentor(token);
          if (!cancelled) setMine(res.data || null);
        } catch {
          if (!cancelled) setMine(null);
        }
        try {
          const res = await CoachingApi.getMySessions(token, { limit: 20 });
          if (!cancelled) setSessions(extractList(res, 'sessions'));
        } catch {
          if (!cancelled) setSessions([]);
        }
      }

      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [token]);

  const openRequest = (mentor) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { notice: 'Sign in to request a mentoring session.' } });
      return;
    }
    if (!isBookableMentor(mentor)) {
      setNotice('This listed profile is not yet bookable. Ask them to apply as a mentor so they get a live profile.');
      return;
    }
    setRequesting(mentor);
    setRequestForm({ topic: '', when: defaultWhen(), duration: 45 });
    setNotice('');
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!requesting) return;
    setRequestBusy(true);
    setNotice('');
    try {
      const res = await CoachingApi.requestSession(token, {
        mentorId: requesting._id,
        title: requestForm.topic ? requestForm.topic.slice(0, 200) : `Session with ${requesting.name}`,
        topic: requestForm.topic,
        scheduledDate: new Date(requestForm.when).toISOString(),
        duration: Number(requestForm.duration || 45),
      });
      setNotice(res.message || 'Request sent.');
      setRequesting(null);
      await loadSessions();
    } catch (err) {
      setNotice(err.message || 'Could not send the request.');
    } finally {
      setRequestBusy(false);
    }
  };

  return (
    <div className="cm">
      <Header />

      <main className="cm-main">
        <header className="cm-head cm-head--row">
          <div>
            <p>Mentorship</p>
            <h1>Talk to people who have done the work.</h1>
          </div>
          {mine?.status === 'approved' ? (
            <Link to="/mentor-dashboard" className="cm-apply">Open mentor studio</Link>
          ) : mine?.status === 'pending' ? (
            <span className="cm-pending">Application pending review</span>
          ) : mine?.status === 'rejected' ? (
            <span className="cm-pending">Application was not approved</span>
          ) : (
            <button
              type="button"
              className="cm-apply"
              onClick={() => {
                if (!isAuthenticated) {
                  navigate('/login', { state: { notice: 'Sign in to apply as a mentor.' } });
                  return;
                }
                setFormOpen((o) => !o);
              }}
            >
              Become a mentor
            </button>
          )}
        </header>

        <ol className="cm-steps">
          <li>Apply</li>
          <li>Admin approves</li>
          <li>You appear here</li>
          <li>Student requests</li>
          <li>Mentor starts the live call</li>
        </ol>

        {formOpen && (
          <form
            className="cm-card cm-apply-form"
            onSubmit={async (e) => {
              e.preventDefault();
              setApplying(true);
              setNotice('');
              try {
                const res = await CoachingApi.createMentor(token, {
                  ...form,
                  expertiseAreas: form.expertiseAreas,
                });
                setMine(res.data);
                setFormOpen(false);
                setNotice(res.message || 'Application submitted.');
              } catch (err) {
                setNotice(err.message || 'Could not submit.');
              } finally {
                setApplying(false);
              }
            }}
          >
            <h3>Apply to mentor</h3>
            <label>Title
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="e.g. Process Engineer" />
            </label>
            <label>Industry
              <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} required />
            </label>
            <label>Years of experience
              <input type="number" min="0" value={form.yearsOfExperience} onChange={(e) => setForm({ ...form, yearsOfExperience: e.target.value })} />
            </label>
            <label>Expertise (comma separated)
              <input value={form.expertiseAreas} onChange={(e) => setForm({ ...form, expertiseAreas: e.target.value })} placeholder="AI, manufacturing, careers" />
            </label>
            <label>Short bio
              <textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} required />
            </label>
            <button type="submit" className="cm-apply" disabled={applying}>{applying ? 'Sending…' : 'Submit for review'}</button>
          </form>
        )}

        {requesting && (
          <form className="cm-card cm-apply-form" onSubmit={submitRequest}>
            <h3>Request a session with {requesting.name}</h3>
            <label>What do you want to work on?
              <textarea rows={3} value={requestForm.topic} onChange={(e) => setRequestForm({ ...requestForm, topic: e.target.value })} required placeholder="Career switch, a design review, interview prep…" />
            </label>
            <label>Preferred time
              <input type="datetime-local" value={requestForm.when} onChange={(e) => setRequestForm({ ...requestForm, when: e.target.value })} required />
            </label>
            <label>Duration (minutes)
              <input type="number" min="20" max="120" value={requestForm.duration} onChange={(e) => setRequestForm({ ...requestForm, duration: e.target.value })} />
            </label>
            <div className="cm-actions">
              <button type="submit" className="cm-apply" disabled={requestBusy}>{requestBusy ? 'Sending…' : 'Send request'}</button>
              <button type="button" className="cm-btn-ghost" onClick={() => setRequesting(null)}>Cancel</button>
            </div>
          </form>
        )}

        {notice && <p className="cm-foot-note">{notice}</p>}

        <section className="cm-grid">
          {loading && mentors.length === 0
            ? [0, 1].map((i) => <div key={i} className="cm-card cm-card--skel" />)
            : mentors.map((m) => (
                <article key={m._id} className="cm-card">
                  <div className="cm-avatar">{(m.name || '?').slice(0, 1)}</div>
                  <h2>{m.name}</h2>
                  <p>{m.title}</p>
                  <ul>
                    {(m.expertise || []).slice(0, 4).map((tag) => (
                      <li key={tag}>{tag}</li>
                    ))}
                  </ul>
                  <div className="cm-actions">
                    {m.profileUrl && (
                      <a href={m.profileUrl} target="_blank" rel="noopener noreferrer">
                        Profile
                      </a>
                    )}
                    {isBookableMentor(m) ? (
                      <button type="button" className="cm-text-btn" onClick={() => openRequest(m)}>
                        Request a session
                      </button>
                    ) : (
                      <span className="cm-muted">Listed — not bookable yet</span>
                    )}
                  </div>
                </article>
              ))}
        </section>

        {token && (
          <section className="cm-block">
            <h3>Your sessions</h3>
            {sessions.length === 0 ? (
              <p>No requests yet. Pick a mentor and send the first one.</p>
            ) : (
              <ul className="cm-sessions">
                {sessions.map((s) => (
                  <li key={s._id}>
                    <span>
                      {s.title || 'Session'}
                      {s.mentor ? ` · ${personName(s.mentor)}` : ''}
                    </span>
                    <span className="cm-session-right">
                      <em>{sessionLabel(s.status)}</em>
                      {s.meetingLink && (
                        <Link to={s.meetingLink} className="cm-join">
                          <Video size={14} /> Join
                        </Link>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {programs.length > 0 && (
          <section className="cm-block">
            <h3>Programs</h3>
            <div className="cm-grid cm-grid--2">
              {programs.map((p) => (
                <article key={p._id || p.title} className="cm-card">
                  <h2>{p.title}</h2>
                  {p.description && <p>{p.description}</p>}
                  <div className="cm-meta">
                    {p.duration && <span>{p.duration}</span>}
                    {p.level && <span>{p.level}</span>}
                    {p.price != null && <strong>${p.price}</strong>}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <p className="cm-foot-note">
          Prefer the community board? <Link to="/qoreconnect/mentorship">Open QoreConnect mentorship <ArrowRight size={12} /></Link>
        </p>
      </main>

      <Footer />
    </div>
  );
};

export default CoachingMentoring;
